import { useState, useCallback } from "react";
import axios from "axios";
import { Loader2, ExternalLink, ArrowLeft, RotateCcw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Topic options (maps to API category values) ──
const TOPICS = [
  { label: "DeFi",         value: "defi" },
  { label: "Governance",   value: "governance" },
  { label: "Funding",      value: "funding" },
  { label: "Security",     value: "security" },
  { label: "Regulation",   value: "regulation_legal" },
  { label: "RWA",          value: "rwa" },
  { label: "AI & crypto",  value: "ai_crypto" },
  { label: "Stablecoins",  value: "stablecoins_payments" },
  { label: "Infrastructure", value: "infrastructure" },
  { label: "Everything",   value: null },
];

const TIME_OPTIONS = [
  { label: "Today",       subLabel: "last 24h",  value: 1 },
  { label: "This week",   subLabel: "7 days",    value: 7 },
  { label: "This month",  subLabel: "30 days",   value: 30 },
  { label: "This quarter",subLabel: "90 days",   value: 90 },
];

const QUICK_ASSETS = ["BTC", "ETH", "SOL", "DOT", "ATOM", "ARB", "AAVE", "ONDO", "TIA"];

// ── Friendly time label ──
const timeLabel = (days) => {
  if (!days) return "___";
  const opt = TIME_OPTIONS.find(o => o.value === days);
  return opt ? opt.label.toLowerCase() : `${days} days`;
};

// ── Date formatting ──
const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0 && diffHours < 24) {
    if (diffHours < 1) return "Just now";
    return `${diffHours}h ago`;
  }
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// ── Category label map ──
const CAT_LABELS = {
  defi: "DeFi", governance: "Governance", funding: "Funding",
  security: "Security", regulation_legal: "Regulation", rwa: "RWA",
  ai_crypto: "AI & crypto", stablecoins_payments: "Stablecoins",
  infrastructure: "Infrastructure", product_launch: "Launch",
  token_events: "Token event", corporate: "Corporate",
  partnership: "Partnership", personnel: "Personnel",
  network_incident: "Incident", nft_gaming: "NFT / Gaming",
};

// ── Result Card ──
const ResultCard = ({ item, dark }) => {
  const border = dark ? "border-[#2a2a3a] hover:border-[#3a3a4a]" : "border-gray-200 hover:border-gray-300";
  const bg = dark ? "bg-[#12121a]" : "bg-white";
  const textPrimary = dark ? "text-[#e0e0e8]" : "text-gray-900";
  const textDim = dark ? "text-[#7a7a8e]" : "text-gray-400";
  const badgeBg = dark ? "bg-[#1a1a26] text-[#7a7a8e] border-[#2a2a3a]" : "bg-gray-50 text-gray-500 border-gray-200";
  const assetCls = dark ? "bg-[#1a1a26] text-emerald-400 border-[#2a2a3a]" : "bg-emerald-50 text-emerald-700 border-emerald-100";

  const catLabel = CAT_LABELS[item.category] || item.category?.replace(/_/g, " ");
  const assets = item.assets ? item.assets.split(",").map(a => a.trim()).filter(Boolean) : [];
  const impColor = {
    high: dark ? "text-emerald-400" : "text-emerald-600",
    medium: dark ? "text-amber-400" : "text-amber-600",
    low: textDim,
  }[item.importance] || textDim;

  return (
    <div className={`rounded-lg border p-4 transition-colors ${bg} ${border}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Meta row */}
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {catLabel && (
              <span className={`text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded-full border ${badgeBg}`}>
                {catLabel}
              </span>
            )}
            {item.importance && (
              <span className={`text-[10px] font-medium uppercase tracking-wide ${impColor}`}>
                {item.importance}
              </span>
            )}
            <span className={`text-[11px] font-mono ml-auto ${textDim}`}>
              {formatDate(item.event_date || item.source_timestamp || item.created_at)}
            </span>
          </div>
          {/* Headline */}
          <p className={`text-sm leading-relaxed ${textPrimary}`}>
            {item.summary || item.raw_text}
          </p>
          {/* Source */}
          {item.source_author && (
            <span className={`text-[11px] font-mono mt-1 block ${textDim}`}>
              @{item.source_author}
            </span>
          )}
          {/* Assets */}
          {assets.length > 0 && (
            <div className="flex gap-1 mt-2.5 flex-wrap">
              {assets.slice(0, 5).map((a, i) => (
                <span key={i} className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${assetCls}`}>
                  {a}
                </span>
              ))}
            </div>
          )}
        </div>
        {/* External link */}
        {item.source_url && (
          <a
            href={item.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex-shrink-0 mt-0.5 transition-colors ${textDim} hover:text-blue-400`}
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════
const AskTheFeed = ({ dark }) => {
  const [topic, setTopic] = useState(null);      // TOPICS item or null
  const [days, setDays] = useState(null);         // number
  const [asset, setAsset] = useState(null);       // string or null (null = skip/all)
  const [customAsset, setCustomAsset] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // ── Theme helpers ──
  const bg = dark ? "bg-[#0c0c14]" : "bg-white";
  const textPrimary = dark ? "text-[#e0e0e8]" : "text-gray-900";
  const textDim = dark ? "text-[#7a7a8e]" : "text-gray-400";
  const border = dark ? "border-[#2a2a3a]" : "border-gray-200";

  // ── Chip style helpers ──
  const chipBase = `
    px-4 py-2.5 rounded-full text-sm font-medium
    border transition-all duration-150 cursor-pointer
    focus:outline-none
  `;
  const chipIdle = dark
    ? "bg-transparent border-[#2a2a3a] text-[#7a7a8e] hover:border-[#4a4a5a] hover:text-[#e0e0e8]"
    : "bg-transparent border-gray-200 text-gray-500 hover:border-gray-400 hover:text-gray-700";
  const chipActive = dark
    ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-400"
    : "bg-emerald-50 border-emerald-400 text-emerald-700";

  // ── Time button style ──
  const timeBtnBase = `
    flex flex-col items-center px-5 py-3 rounded-xl
    border transition-all duration-150 cursor-pointer focus:outline-none
    min-w-[90px]
  `;
  const timeBtnIdle = dark
    ? "bg-transparent border-[#2a2a3a] hover:border-[#4a4a5a]"
    : "bg-transparent border-gray-200 hover:border-gray-400";
  const timeBtnActive = dark
    ? "bg-emerald-500/10 border-emerald-500/50"
    : "bg-emerald-50 border-emerald-400";

  // ── Sentence ──
  const topicDisplay = topic
    ? <span className={`font-semibold underline decoration-dashed underline-offset-4 ${dark ? "text-emerald-400 decoration-emerald-500/50" : "text-emerald-600 decoration-emerald-400"}`}>{topic.label.toLowerCase()}</span>
    : <span className={`underline decoration-dashed underline-offset-4 ${dark ? "decoration-[#4a4a5a] text-[#4a4a5a]" : "decoration-gray-300 text-gray-300"}`}>______</span>;

  const timeDisplay = days
    ? <span className={`font-semibold underline decoration-dashed underline-offset-4 ${dark ? "text-emerald-400 decoration-emerald-500/50" : "text-emerald-600 decoration-emerald-400"}`}>{timeLabel(days)}</span>
    : <span className={`underline decoration-dashed underline-offset-4 ${dark ? "decoration-[#4a4a5a] text-[#4a4a5a]" : "decoration-gray-300 text-gray-300"}`}>______</span>;

  const canSubmit = topic !== null && days !== null;

  // ── Fetch ──
  const fetchBriefing = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (topic && topic.value) params.set("category", topic.value);
      if (days) params.set("days", days.toString());
      if (asset) params.set("assets", asset);
      params.set("limit", "30");
      params.set("offset", "0");
      const res = await axios.get(`${BACKEND_URL}/api/public/feed?${params.toString()}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setShowResults(true);
    } catch (err) {
      console.error("Briefing fetch failed:", err);
    } finally {
      setLoading(false);
    }
  }, [topic, days, asset]);

  const reset = () => {
    setTopic(null);
    setDays(null);
    setAsset(null);
    setCustomAsset("");
    setShowResults(false);
    setItems([]);
    setTotal(0);
  };

  // ────────────────────────────────────
  // RESULTS VIEW
  // ────────────────────────────────────
  if (showResults) {
    const summaryAsset = asset ? ` · ${asset}` : "";
    const summaryTime = TIME_OPTIONS.find(o => o.value === days);

    return (
      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Back */}
        <button
          onClick={() => setShowResults(false)}
          className={`flex items-center gap-1.5 text-sm mb-6 transition-colors ${textDim} hover:${textPrimary}`}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to selection
        </button>

        {/* Briefing header */}
        <div className="mb-6">
          <p className={`text-[11px] font-mono uppercase tracking-widest mb-2 ${textDim}`}>Your briefing</p>
          <h2 className={`text-2xl font-bold tracking-tight ${textPrimary}`}>
            {topic?.label || "All news"}{" "}
            <span className={dark ? "text-[#7a7a8e]" : "text-gray-400"}>
              — {summaryTime?.subLabel || `${days}d`}{summaryAsset}
            </span>
          </h2>
          <p className={`text-sm mt-1 ${textDim}`}>
            {total} {total === 1 ? "item" : "items"} found
          </p>
        </div>

        {/* Items */}
        {items.length === 0 ? (
          <div className={`text-center py-16 ${textDim}`}>
            <p className="text-sm">No items found for this briefing.</p>
            <p className={`text-xs mt-1 ${textDim}`}>Try broadening your topic or time window.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {items.map(item => (
              <ResultCard key={item.id} item={item} dark={dark} />
            ))}
          </div>
        )}

        {/* Start over */}
        <div className="mt-10 text-center">
          <button
            onClick={reset}
            className={`flex items-center gap-1.5 text-sm mx-auto transition-colors ${textDim} hover:text-emerald-400`}
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Start over
          </button>
        </div>
      </div>
    );
  }

  // ────────────────────────────────────
  // ASK FLOW
  // ────────────────────────────────────
  return (
    <div className="max-w-2xl mx-auto px-6 py-10">

      {/* ── Natural language sentence ── */}
      <p className={`text-xl sm:text-2xl font-semibold mb-10 leading-snug ${textPrimary}`}>
        Show me {topicDisplay} news from the {timeDisplay}
      </p>

      {/* ── Step 1: Topic ── */}
      <div className="mb-8">
        <p className={`text-[10px] font-semibold uppercase tracking-[0.12em] mb-3 flex items-center gap-2 ${textDim}`}>
          <span className={`w-4 h-4 rounded-full text-[9px] flex items-center justify-center border font-bold ${
            topic
              ? dark ? "border-emerald-500/50 text-emerald-400" : "border-emerald-400 text-emerald-600"
              : dark ? "border-[#3a3a4a] text-[#4a4a5a]" : "border-gray-300 text-gray-400"
          }`}>1</span>
          What are you interested in?
        </p>
        <div className="flex flex-wrap gap-2">
          {TOPICS.map(t => (
            <button
              key={t.value ?? "all"}
              onClick={() => setTopic(topic?.value === t.value ? null : t)}
              className={`${chipBase} ${topic?.value === t.value ? chipActive : chipIdle}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Step 2: Time window ── */}
      <div className="mb-8">
        <p className={`text-[10px] font-semibold uppercase tracking-[0.12em] mb-3 flex items-center gap-2 ${textDim}`}>
          <span className={`w-4 h-4 rounded-full text-[9px] flex items-center justify-center border font-bold ${
            days
              ? dark ? "border-emerald-500/50 text-emerald-400" : "border-emerald-400 text-emerald-600"
              : dark ? "border-[#3a3a4a] text-[#4a4a5a]" : "border-gray-300 text-gray-400"
          }`}>2</span>
          How far back?
        </p>
        <div className="flex flex-wrap gap-2">
          {TIME_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setDays(days === opt.value ? null : opt.value)}
              className={`${timeBtnBase} ${days === opt.value ? timeBtnActive : timeBtnIdle}`}
            >
              <span className={`text-sm font-semibold ${
                days === opt.value
                  ? dark ? "text-emerald-400" : "text-emerald-700"
                  : textPrimary
              }`}>{opt.label}</span>
              <span className={`text-[10px] mt-0.5 font-mono ${textDim}`}>{opt.subLabel}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Step 3: Asset (optional) ── */}
      <div className="mb-10">
        <p className={`text-[10px] font-semibold uppercase tracking-[0.12em] mb-3 flex items-center gap-2 ${textDim}`}>
          <span className={`w-4 h-4 rounded-full text-[9px] flex items-center justify-center border font-bold ${
            dark ? "border-[#3a3a4a] text-[#4a4a5a]" : "border-gray-300 text-gray-400"
          }`}>3</span>
          Focus on a specific asset?
          <span className={`normal-case tracking-normal text-[10px] font-normal ${textDim}`}>(optional)</span>
        </p>
        <div className="flex flex-wrap gap-2">
          {QUICK_ASSETS.map(a => (
            <button
              key={a}
              onClick={() => setAsset(asset === a ? null : a)}
              className={`px-3.5 py-2 rounded-full text-xs font-mono border transition-all duration-150 cursor-pointer focus:outline-none ${
                asset === a ? chipActive : chipIdle
              }`}
            >
              {a}
            </button>
          ))}
          <button
            onClick={() => setAsset(null)}
            className={`px-4 py-2 rounded-full text-xs border transition-all duration-150 cursor-pointer focus:outline-none ${
              asset === null
                ? chipActive
                : chipIdle
            }`}
          >
            Skip — show all
          </button>
        </div>

        {/* Custom asset input */}
        <div className="mt-3">
          <input
            value={customAsset}
            onChange={e => setCustomAsset(e.target.value.toUpperCase())}
            onKeyDown={e => {
              if (e.key === "Enter" && customAsset.trim()) {
                setAsset(customAsset.trim());
                setCustomAsset("");
              }
            }}
            placeholder="Or type a ticker and press Enter…"
            className={`h-9 w-full max-w-xs px-3 text-xs rounded-lg border outline-none transition-colors font-mono
              ${dark
                ? "bg-[#12121a] border-[#2a2a3a] text-[#e0e0e8] placeholder-[#4a4a5a] focus:border-emerald-500/40"
                : "bg-gray-50 border-gray-200 text-gray-800 placeholder-gray-400 focus:border-emerald-400"
              }`}
          />
        </div>
      </div>

      {/* ── Divider ── */}
      <div className={`border-t mb-8 ${border}`} />

      {/* ── CTA ── */}
      <div className="flex flex-col items-stretch gap-3 max-w-xs">
        <button
          onClick={fetchBriefing}
          disabled={!canSubmit || loading}
          className={`h-12 rounded-xl text-sm font-semibold transition-all duration-150 flex items-center justify-center gap-2
            ${canSubmit && !loading
              ? "bg-emerald-500 hover:bg-emerald-400 text-[#0a0a0f] shadow-lg shadow-emerald-500/20 cursor-pointer"
              : dark
                ? "bg-[#1a1a26] text-[#3a3a4a] border border-[#2a2a3a] cursor-not-allowed"
                : "bg-gray-100 text-gray-300 border border-gray-200 cursor-not-allowed"
            }`}
        >
          {loading ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Building briefing…</>
          ) : (
            "Get my briefing"
          )}
        </button>
        <button
          onClick={reset}
          className={`text-xs text-center transition-colors ${textDim} hover:text-emerald-400`}
        >
          Start over
        </button>
      </div>
    </div>
  );
};

export default AskTheFeed;
